import React, { useState, useMemo } from "react";
import CustomButton from "components/common/Button/CustomButton";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { ReactMultiEmail } from "react-multi-email";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import DateRangeWithRegister from "components/common/DateRangePicker/DateRangePickerWithRegister";
import moment from "moment";

const MultipleEmailModal = ({
  showModal,
  closeModalHandler,
  onClick,
  defaultData = [],
  emailSendLoader,
  spFormTempId,
}) => {
  const [emails, setEmails] = useState([]);

  const uniqueSentEmails = useMemo(() => {
    const seen = new Set();
    return defaultData
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .filter(
        (i) =>
          i?.email_sent === true &&
          !seen.has(i.email_address) &&
          seen.add(i.email_address)
      );
  }, [defaultData]);

  const { control, getValues, reset } = useForm();

  const modalFooter = () => {
    return (
      <>
        <CustomButton
          isDisabled={emailSendLoader}
          variant='danger'
          label='Close'
          clickHandler={() => {
            closeModalHandler();
            setEmails([]);
            reset();
          }}
        />
        <CustomButton
          showLoading={emailSendLoader}
          isDisabled={emailSendLoader}
          label='Send'
          clickHandler={() => {
            const expiry_date = getValues("expiry_date");
            if (emails?.length > 0) {
              onClick(emails, expiry_date);
            } else {
              toast("Please enter at least one email!");
            }
          }}
        />
      </>
    );
  };

  return (
    <ModalSkeleton
      isOpen={showModal}
      title={"Send emails"}
      closeModal={() => {
        closeModalHandler();
        setEmails([]);
        reset();
      }}
      modalFooter={modalFooter()}
      cssClass='w-[90%] md:w-[50%]'
    >
      <div className='p-2 pt-0'>
        <div className='flex items-center mb-2'>
          <label className='text-gray-700 text-sm font-medium mr-4'>
            Emails (Enter one email, press "Enter" to add another. Repeat for
            more emails.)
          </label>

          <input
            type='text'
            tabIndex={0}
            aria-hidden='true'
            ref={(el) => el && el.blur()}
            className='absolute -left-[9999px] w-px h-px opacity-0'
          />

          <div className='flex flex-col mb-4'>
            <label className='text-gray-700 text-sm font-medium mb-1'>
              Select Expiry Date
            </label>
            <Controller
              name='expiry_date'
              control={control}
              render={({ field, fieldState }) => (
                <DateRangeWithRegister
                  isSingleDate
                  field={field}
                  fieldState={fieldState}
                  placeholder='Select Expiry Date (default: 7 days)'
                  showCalendarIcon
                  minDate={moment().add(1, "days").toDate()}
                  maxDate={moment().add(30, "days").toDate()}
                />
              )}
            />
          </div>
        </div>

        {/* Emails Input */}
        <div className='py-2'>
          <ReactMultiEmail
            placeholder='Input your emails'
            emails={emails}
            onChange={(_emails) => setEmails(_emails)}
            autoFocus={true}
            getLabel={(email, index, removeEmail) => (
              <div data-tag key={index}>
                <div data-tag-item>{email}</div>
                <span
                  className='hover:bg-red-200 p-1'
                  data-tag-handle
                  onClick={() => removeEmail(index)}
                >
                  ×
                </span>
              </div>
            )}
          />
        </div>

        {/* Previously sent emails */}
        <div className='space-y-4'>
          {uniqueSentEmails.length > 0 && (
            <span className='text-gray-700 text-sm font-semibold'>
              This form has already been sent to the following email addresses
            </span>
          )}
          {uniqueSentEmails.map((item, index) => (
            <div
              className='text-gray-700 text-sm font-medium flex justify-start items-center space-x-2'
              key={item.email_address}
            >
              <span className='w-[5%]'>{index + 1}:</span>
              <span className='w-[85%]'>{item.email_address}</span>
              <span className='w-[10%]'>
                {item.email_sent ? (
                  <AiOutlineCheckCircle className='w-5 h-5' color={"green"} />
                ) : (
                  <AiOutlineCloseCircle className='w-5 h-5' color={"red"} />
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </ModalSkeleton>
  );
};

export default MultipleEmailModal;
